/**
 * Announcements (TMMPDM-0110; TMMAPI-0181 item 32a): the Support page's third tab. The product
 * admin's news to every customer, or to chosen tenants: title, kind, audience, state as sent, publish
 * and expiry times, pinned and pop-up, the related feature request, and how many people have read
 * it. Filters (state, kind) and the page live in the URL; the list is server-paged, newest first.
 *
 * Until tmm-api deploys item 32a the list answers a bare 404, and the tab says "Announcements aren't
 * available yet." with no retry.
 */
import { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Link,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useCopy } from '@/hooks/useCopy';
import { positiveInt, text, useUrlState, type UrlSchema } from '@/hooks/useUrlState';
import { adminApi } from '@/api/tmm/admin';
import { isActingAdminRevoked } from '@/api/errors';
import { ANNOUNCEMENT_KINDS, ANNOUNCEMENT_STATES, type AnnouncementPage } from '@/api/tmm/types';
import { EmptyState, ErrorState } from '@/components/page';
import { stickyTableContainerSx } from '@/components/ui';
import { formatInstant, formatNumber } from '@/format';
import { StatusChip } from '@/status';
import { ticketRef } from './tickets';
import {
  announcementPath,
  audienceLabel,
  isAnnouncementsNotDeployed,
  kindLabel,
  stateLabel,
} from './announcements';

const PAGE_SIZE = 20;
const COLUMNS = 9;

type ListView = { state: string; kind: string; page: number };

const VIEW: UrlSchema<ListView> = {
  state: { default: '', parse: text },
  kind: { default: '', parse: text },
  page: { default: 1, parse: positiveInt },
};

const headerCellSx = { fontWeight: 600 } as const;

export function AnnouncementsList() {
  const { t } = useCopy();
  const { i18n } = useTranslation();
  const locale = i18n.language;
  const [view, setView] = useUrlState(VIEW);
  // A filter from a hand-edited link that names no known state or kind is no filter.
  const state = (ANNOUNCEMENT_STATES as readonly string[]).includes(view.state) ? view.state : '';
  const kind = (ANNOUNCEMENT_KINDS as readonly string[]).includes(view.kind) ? view.kind : '';
  const page = view.page - 1;
  const [reloadToken, setReloadToken] = useState(0);
  const requestKey = `${state}:${kind}:${page}:${reloadToken}`;
  const [response, setResponse] = useState<{
    requestKey: string | null;
    result: AnnouncementPage | null;
    failure: unknown;
  }>({ requestKey: null, result: null, failure: null });

  useEffect(() => {
    let cancelled = false;
    adminApi
      .listAnnouncements({
        ...(state ? { state } : {}),
        ...(kind ? { kind } : {}),
        page,
        size: PAGE_SIZE,
      })
      .then((result) => {
        if (!cancelled) setResponse({ requestKey, result, failure: null });
      })
      .catch((failure: unknown) => {
        if (!cancelled) setResponse({ requestKey, result: null, failure: failure ?? 'failed' });
      });
    return () => {
      cancelled = true;
    };
  }, [state, kind, page, requestKey]);

  const current = response.requestKey === requestKey;
  const loading = !current;
  const failure = current ? response.failure : null;
  const result = current ? response.result : null;
  const rows = result?.announcements ?? [];
  const totalPages = result?.totalPages ?? 0;
  const pageNumber = (result?.page ?? page) + 1;
  const filtered = state !== '' || kind !== '' || view.page > 1;

  if (failure !== null && isAnnouncementsNotDeployed(failure)) {
    return (
      <EmptyState
        surface
        title={t('announcements.notAvailable', "Announcements aren't available yet.")}
      />
    );
  }

  return (
    <Box>
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        spacing={2}
        useFlexGap
        sx={{ mb: 2, flexWrap: 'wrap', alignItems: { xs: 'stretch', md: 'flex-start' } }}
      >
        <TextField
          select
          size="small"
          label={t('announcements.filter.state', 'State')}
          value={state === '' ? 'ALL' : state}
          onChange={(event) =>
            setView({ state: event.target.value === 'ALL' ? '' : event.target.value })
          }
          sx={{ minWidth: 180 }}
        >
          <MenuItem value="ALL">{t('announcements.filter.allStates', 'All states')}</MenuItem>
          {ANNOUNCEMENT_STATES.map((code) => (
            <MenuItem key={code} value={code}>
              {stateLabel(code, t)}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          select
          size="small"
          label={t('announcements.filter.kind', 'Kind')}
          value={kind === '' ? 'ALL' : kind}
          onChange={(event) =>
            setView({ kind: event.target.value === 'ALL' ? '' : event.target.value })
          }
          sx={{ minWidth: 180 }}
        >
          <MenuItem value="ALL">{t('announcements.filter.allKinds', 'All kinds')}</MenuItem>
          {ANNOUNCEMENT_KINDS.map((code) => (
            <MenuItem key={code} value={code}>
              {kindLabel(code, t)}
            </MenuItem>
          ))}
        </TextField>
        <Box sx={{ flex: 1 }} />
        <Button variant="contained" component={RouterLink} to={announcementPath('new')}>
          {t('announcements.new', 'New announcement')}
        </Button>
      </Stack>

      {failure ? (
        <ErrorState
          error={failure}
          message={t('announcements.loadError', 'Could not load announcements.')}
          {...(isActingAdminRevoked(failure)
            ? {}
            : { onRetry: () => setReloadToken((n) => n + 1) })}
        />
      ) : !loading && rows.length === 0 ? (
        <EmptyState
          surface
          title={
            filtered
              ? t('announcements.emptyFiltered', 'No announcements match these filters.')
              : t('announcements.empty', 'No announcements yet.')
          }
          {...(filtered
            ? { filtered: { onClear: () => setView({ state: '', kind: '', page: 1 }) } }
            : {})}
        />
      ) : (
        <TableContainer
          component={Paper}
          variant="outlined"
          role="region"
          aria-label={t('announcements.regionAria', 'Announcements list')}
          aria-busy={loading ? 'true' : 'false'}
          sx={stickyTableContainerSx}
        >
          <Table
            size="small"
            stickyHeader
            aria-label={t('tickets.tab.announcements', 'Announcements')}
          >
            <TableHead>
              <TableRow>
                <TableCell sx={headerCellSx}>{t('announcements.col.title', 'Title')}</TableCell>
                <TableCell sx={headerCellSx}>{t('announcements.col.kind', 'Kind')}</TableCell>
                <TableCell sx={headerCellSx}>
                  {t('announcements.col.audience', 'Audience')}
                </TableCell>
                <TableCell sx={headerCellSx}>{t('announcements.col.state', 'State')}</TableCell>
                <TableCell sx={headerCellSx}>
                  {t('announcements.col.publishAt', 'Publishes')}
                </TableCell>
                <TableCell sx={headerCellSx}>
                  {t('announcements.col.expiresAt', 'Expires')}
                </TableCell>
                <TableCell sx={headerCellSx}>
                  {t('announcements.col.shownAs', 'Shown as')}
                </TableCell>
                <TableCell sx={headerCellSx}>
                  {t('announcements.col.related', 'Related ticket')}
                </TableCell>
                <TableCell sx={headerCellSx} align="right">
                  {t('announcements.col.reads', 'Reads')}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={COLUMNS} align="center" sx={{ py: 4 }}>
                    <Typography variant="body2" color="text.secondary">
                      {t('common.loading', 'Loading')}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                rows.map((row) => {
                  const shownAs = [
                    ...(row.pinned ? [t('announcements.pinned', 'Pinned')] : []),
                    ...(row.popup ? [t('announcements.popup', 'Pop-up')] : []),
                  ];
                  const related =
                    row.relatedTicket === undefined
                      ? null
                      : ticketRef('FEATURE_REQUEST', row.relatedTicket);
                  return (
                    <TableRow key={row.id} hover>
                      <TableCell sx={{ maxWidth: 320, overflowWrap: 'anywhere' }}>
                        <Link component={RouterLink} to={announcementPath(row.id)}>
                          {row.title}
                        </Link>
                      </TableCell>
                      <TableCell>
                        <StatusChip status={row.kind} label={kindLabel(row.kind, t)} />
                      </TableCell>
                      <TableCell>{audienceLabel(row, t)}</TableCell>
                      <TableCell>
                        <StatusChip status={row.state} label={stateLabel(row.state, t)} />
                      </TableCell>
                      <TableCell>
                        {row.publishAt === undefined ? '—' : formatInstant(row.publishAt, locale)}
                      </TableCell>
                      <TableCell>
                        {row.expiresAt === undefined ? '—' : formatInstant(row.expiresAt, locale)}
                      </TableCell>
                      <TableCell>{shownAs.length === 0 ? '—' : shownAs.join(' · ')}</TableCell>
                      <TableCell sx={(theme) => ({ ...theme.typography.mono })}>
                        {related === null ? (
                          '—'
                        ) : (
                          <Link
                            component={RouterLink}
                            to={`/tickets/${encodeURIComponent(related)}`}
                          >
                            {related}
                          </Link>
                        )}
                      </TableCell>
                      <TableCell align="right">{formatNumber(row.readCount, locale)}</TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {!failure && (loading || rows.length > 0) ? (
        <Stack
          direction="row"
          spacing={2}
          sx={{ mt: 2, alignItems: 'center', justifyContent: 'flex-end' }}
        >
          <Button
            variant="outlined"
            disabled={loading || (result?.page ?? page) <= 0}
            onClick={() => setView({ page: Math.max(1, view.page - 1) })}
          >
            {t('tenants.pagination.previous', 'Previous')}
          </Button>
          <Typography variant="body2" aria-live="polite">
            {totalPages === 0
              ? null
              : t('tenants.pagination.pageOf', 'Page {{page, number}} of {{total, number}}', {
                  page: pageNumber,
                  total: totalPages,
                })}
          </Typography>
          <Button
            variant="outlined"
            disabled={loading || totalPages === 0 || (result?.page ?? page) + 1 >= totalPages}
            onClick={() => setView({ page: view.page + 1 })}
          >
            {t('tenants.pagination.next', 'Next')}
          </Button>
        </Stack>
      ) : null}
    </Box>
  );
}
